import React, { useEffect, useRef } from 'react';
import { MapPin, Navigation } from 'lucide-react';
import { useGeolocation } from '../hooks/useGeolocation';
import { useTranslation } from 'react-i18next';

export const MapView = ({ 
  markers = [], 
  providerLocation = null,
  className = '',
  onMapClick,
  center,
  zoom = 14,
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const providerMarkerRef = useRef(null);
  const { position, loading: geoLoading } = useGeolocation();
  const { t } = useTranslation();

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    if (typeof window === 'undefined' || !window.L) return;

    const L = window.L;
    const defaultCenter = center || 
      (position ? [position.latitude, position.longitude] : [60.1699, 24.9384]);

    const map = L.map(mapRef.current, {
      zoomControl: false,
    }).setView(defaultCenter, zoom);

    L.control.zoom({ position: 'topright' }).addTo(map);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    if (onMapClick) {
      map.on('click', (e) => {
        onMapClick({ latitude: e.latlng.lat, longitude: e.latlng.lng });
      });
    }

    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  // Update map center when position changes
  useEffect(() => {
    if (!mapInstanceRef.current || !position) return;
    if (!center) {
      mapInstanceRef.current.setView([position.latitude, position.longitude], zoom);
    }
  }, [position]);

  // Update center prop
  useEffect(() => {
    if (!mapInstanceRef.current || !center) return;
    mapInstanceRef.current.setView(center, zoom);
  }, [center, zoom]);

  // Update markers
  useEffect(() => {
    if (!mapInstanceRef.current || typeof window === 'undefined' || !window.L) return;
    const L = window.L;

    // Clear old markers
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    // Add user location marker
    if (position) {
      const userIcon = L.divIcon({
        className: 'user-location-marker',
        html: `<div style="width:16px;height:16px;background:#3d9168;border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      });
      const userMarker = L.marker([position.latitude, position.longitude], { icon: userIcon })
        .addTo(mapInstanceRef.current)
        .bindPopup(t('map.yourLocation'));
      markersRef.current.push(userMarker);
    }

    // Add custom markers
    markers.forEach(m => {
      const color = m.type === 'pickup' ? '#3d9168' : m.type === 'dropoff' ? '#ef4444' : '#3b82f6';
      const icon = L.divIcon({
        className: 'custom-marker',
        html: `<div style="width:12px;height:12px;background:${color};border:2px solid white;border-radius:50%;box-shadow:0 2px 4px rgba(0,0,0,0.3);"></div>`,
        iconSize: [12, 12],
        iconAnchor: [6, 6],
      });
      const marker = L.marker([m.latitude, m.longitude], { icon })
        .addTo(mapInstanceRef.current);
      if (m.label) marker.bindPopup(m.label);
      markersRef.current.push(marker);
    });
  }, [markers, position]);

  // Update provider location marker
  useEffect(() => {
    if (!mapInstanceRef.current || typeof window === 'undefined' || !window.L) return;
    const L = window.L;

    if (providerMarkerRef.current) {
      providerMarkerRef.current.remove();
      providerMarkerRef.current = null;
    }

    if (providerLocation) {
      const providerIcon = L.divIcon({
        className: 'provider-marker',
        html: `<div style="width:20px;height:20px;background:#2563eb;border:3px solid white;border-radius:50%;box-shadow:0 2px 8px rgba(37,99,235,0.5);display:flex;align-items:center;justify-content:center;">
          <div style="width:6px;height:6px;background:white;border-radius:50%;"></div>
        </div>`,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });
      providerMarkerRef.current = L.marker(
        [providerLocation.latitude, providerLocation.longitude],
        { icon: providerIcon }
      ).addTo(mapInstanceRef.current)
        .bindPopup(t('map.providerLocation'));
    }
  }, [providerLocation]);

  return (
    <div className={`relative ${className}`}>
      <div ref={mapRef} className="w-full h-full" />
      {geoLoading && (
        <div className="absolute top-3 left-3 bg-white rounded-lg px-3 py-1.5 shadow-md text-xs text-gray-600 flex items-center gap-2 z-[1000]">
          <div className="animate-spin h-3 w-3 border-2 border-primary-500 border-t-transparent rounded-full" />
          {t('common.loading')}
        </div>
      )}
    </div>
  );
};
